/**
 * `/account/edit`, the fan's own profile form. The file is named
 * `account_.edit` so this route needs no `/account` parent. That was
 * originally because `/account` did not exist. ISSUE-485 has since built it,
 * and the trailing underscore is what keeps these two routes independent, so
 * do not rename the file to nest it. `displayName` is read-only because the
 * server derives it from the first and last name. The photo is not:
 * `EditProfilePhotoField` drives the shipped `useAvatarUpload` hook. There is
 * no way to REMOVE a photo, because `profile-api` has no endpoint that clears
 * `photoUrl` (ISSUE-125 dropped the field from `/profile/update` and
 * `/avatar/set` is the only writer). This form has no field for which clock
 * fan-facing times use. A game's date and kickoff
 * time always follow the viewer's own current device timezone, resolved
 * fresh on every read by `resolveViewerTimeZone()`, never stored on this
 * profile (Q271, `docs/features/contest-pickem/overview.md` rule 9). A
 * round's own lock deadline and grouping are the one exception, staying on
 * the pool's fixed anchor timezone so the deadline is the same instant for
 * every entrant, and that is not a fan facing display time either, so this
 * form has no say over it.
 */
import { useIdentityGet } from "@sportsmet/auth-sdk/private";
import {
  useProfileGet,
  useProfileUpdate,
  useUsernameCheck,
  useUsernameClaim,
} from "@sportsmet/profile-sdk/private";
import { confirmDestructive } from "@sportsmet/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../errors/apiErrorBus";
import { EditProfileFields } from "../features/editProfile/EditProfileFields";
import {
  ACCOUNT_ROUTE,
  buildProfileUpdateBody,
  DISCARD_CONFIRM_BODY,
  DISCARD_CONFIRM_LABEL,
  DISCARD_CONFIRM_TITLE,
  EDIT_PROFILE_HEADING,
  hasUnsavedChanges,
  isUsernameChanged,
  LOAD_FAILURE_BODY,
  LOAD_FAILURE_HEADLINE,
  NO_PROFILE_BODY,
  NO_PROFILE_HEADLINE,
  normalizeUsername,
  toFormState,
  type typeEditProfileErrors,
  type typeEditProfileForm,
  UNDER_AGE_ERROR,
  USERNAME_FORMAT_ERROR,
  USERNAME_TAKEN_ERROR,
  usernameWindowHint,
  validateEditProfile,
} from "../features/editProfile/editProfileLogic";
import { EditProfilePhotoField } from "../features/editProfile/EditProfilePhotoField";
import { EmailBlock } from "../features/editProfile/EmailBlock";
import { IdentityBlock } from "../features/editProfile/IdentityBlock";
import { UsernameField } from "../features/editProfile/UsernameField";
import { RoutePendingScreen } from "../shell/RoutePendingScreen";
import { SectionEmptyState, SectionScreen } from "../shell/SectionScreen";

const EditProfileScreen = (): ReactNode => {
  const profileQuery = useProfileGet({});
  const identity = useIdentityGet({});
  const update = useProfileUpdate();
  const claim = useUsernameClaim();
  const check = useUsernameCheck();
  const navigate = useNavigate();

  const profile = profileQuery.data?.data?.profile ?? null;
  const email = identity.data?.data?.email ?? null;

  const [form, setForm] = useState<typeEditProfileForm | null>(null);
  const [original, setOriginal] = useState<typeEditProfileForm | null>(null);
  const [errors, setErrors] = useState<typeEditProfileErrors>({});
  const [availability, setAvailability] = useState<
    "available" | "taken" | "invalidFormat" | null
  >(null);

  useEffect(() => {
    if (profile !== null && original === null) {
      setForm(toFormState(profile));
      setOriginal(toFormState(profile));
    }
  }, [profile]);

  useEffect(() => {
    const code = extractErrorCode(profileQuery.data);
    if (code !== null)
      showApiError(profileQuery.data, undefined, profileQuery.dataUpdatedAt);
  }, [profileQuery.dataUpdatedAt]);

  useEffect(() => {
    const code = extractErrorCode(identity.data);
    if (code !== null)
      showApiError(identity.data, undefined, identity.dataUpdatedAt);
  }, [identity.dataUpdatedAt]);

  useEffect(() => {
    if (form === null || original === null) return;
    if (form.username === original.username || form.username === "") {
      setAvailability(null);
      return;
    }
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const result = await check.mutateAsync({
            username: normalizeUsername(form.username),
          });
          const data = result.data;
          if (data?.isAvailable === true) {
            setAvailability("available");
          } else if (data?.unavailableReason === "taken") {
            setAvailability("taken");
          } else if (data?.unavailableReason === "invalidFormat") {
            setAvailability("invalidFormat");
          } else {
            setAvailability(null);
          }
        } catch {
          setAvailability(null);
        }
      })();
    }, 400);
    return () => {
      clearTimeout(timer);
    };
  }, [form?.username]);

  async function handleSave(): Promise<void> {
    if (form === null || original === null) return;
    const nextErrors = validateEditProfile({ form, now: new Date() });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    if (!hasUnsavedChanges({ form, original })) {
      void navigate({ to: ACCOUNT_ROUTE });
      return;
    }
    if (isUsernameChanged({ form, original }) && availability === "taken") {
      // The inline check already told the fan this username is gone. Firing
      // the claim anyway would only earn a 409 nobody reads, since the
      // response repeats a sentence already on screen. Refuse here instead.
      setErrors({ username: USERNAME_TAKEN_ERROR });
      return;
    }
    try {
      if (isUsernameChanged({ form, original })) {
        const claimed = await claim.mutateAsync({
          username: normalizeUsername(form.username),
        });
        if (claimed.error !== null && claimed.error !== undefined) {
          if (claimed.error.code === "CONFLICT") {
            setErrors({ username: USERNAME_TAKEN_ERROR });
          } else if (claimed.error.code === "RATE_LIMITED") {
            const availableAt =
              claimed.error.details?.usernameChangeAvailableAt;
            setErrors({
              username: usernameWindowHint(
                typeof availableAt === "string" ? availableAt : "",
              ),
            });
          } else if (claimed.error.code === "VALIDATION_ERROR") {
            setErrors({ username: USERNAME_FORMAT_ERROR });
          } else {
            showApiError(claimed);
          }
          return;
        }
      }

      const body = buildProfileUpdateBody({ form, original });
      if (Object.keys(body).length > 0) {
        const saved = await update.mutateAsync(body);
        if (saved.error !== null && saved.error !== undefined) {
          if (String(saved.error.details?.reason) === "belowMinimumAge") {
            setErrors({ birthday: UNDER_AGE_ERROR });
            return;
          }
          showApiError(saved);
          return;
        }
      }

      void navigate({ to: ACCOUNT_ROUTE });
    } catch {
      showOfflineError();
    }
  }

  async function handleCancel(): Promise<void> {
    if (form === null || original === null) return;
    if (!hasUnsavedChanges({ form, original })) {
      void navigate({ to: ACCOUNT_ROUTE });
      return;
    }
    const confirmed = await confirmDestructive({
      title: DISCARD_CONFIRM_TITLE,
      body: DISCARD_CONFIRM_BODY,
      confirmLabel: DISCARD_CONFIRM_LABEL,
    });
    if (!confirmed) return;
    void navigate({ to: ACCOUNT_ROUTE });
  }

  const isSaving = update.isPending || claim.isPending;

  if (profileQuery.isPending || identity.isPending) {
    return <RoutePendingScreen />;
  }

  if (profileQuery.data?.error) {
    return (
      <SectionScreen heading={EDIT_PROFILE_HEADING}>
        <div data-testid="edit-profile-error">
          <SectionEmptyState
            headline={LOAD_FAILURE_HEADLINE}
            body={LOAD_FAILURE_BODY}
          />
        </div>
      </SectionScreen>
    );
  }

  if (profile === null) {
    return (
      <SectionScreen heading={EDIT_PROFILE_HEADING}>
        <div data-testid="edit-profile-no-profile">
          <SectionEmptyState
            headline={NO_PROFILE_HEADLINE}
            body={NO_PROFILE_BODY}
          />
        </div>
      </SectionScreen>
    );
  }

  if (form === null || original === null) {
    return <RoutePendingScreen />;
  }

  return (
    <SectionScreen heading={EDIT_PROFILE_HEADING}>
      <div data-testid="edit-profile-screen" className="flex flex-col gap-4">
        <EditProfilePhotoField
          displayName={profile.displayName}
          photoUrl={profile.photoUrl ?? null}
        />
        <IdentityBlock displayName={profile.displayName} />
        <UsernameField
          value={form.username}
          onChange={(next) => {
            setForm({ ...form, username: next });
          }}
          changeAvailableAt={profile.usernameChangeAvailableAt ?? null}
          availability={availability}
          {...(errors.username === undefined ? {} : { error: errors.username })}
        />
        <EditProfileFields
          form={form}
          errors={errors}
          isSaving={isSaving}
          onFieldChange={(field, next) => {
            setForm({ ...form, [field]: next });
          }}
          onSave={() => {
            void handleSave();
          }}
          onCancel={() => {
            void handleCancel();
          }}
          emailSlot={<EmailBlock email={email} />}
        />
      </div>
    </SectionScreen>
  );
};

export const Route = createFileRoute("/account_/edit")({
  beforeLoad: requireSignedIn,
  component: EditProfileScreen,
});
